import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { banksApi } from '../../services/adminApi';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Sortable Item Component
const SortableItem = ({ bank, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: bank.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tableCellStyle = {
    padding: '1rem 1.5rem',
    fontSize: '0.875rem',
    color: '#111827',
    borderBottom: '1px solid #e5e7eb'
  };

  const statusButtonStyle = (isActive) => ({
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.25rem 0.625rem',
    borderRadius: '9999px',
    fontSize: '0.75rem',
    fontWeight: '500',
    backgroundColor: isActive ? '#dcfce7' : '#fee2e2',
    color: isActive ? '#166534' : '#dc2626',
    border: 'none',
    cursor: 'pointer',
    transition: 'background-color 0.2s, color 0.2s'
  });

  const actionLinkStyle = {
    color: '#2563eb',
    textDecoration: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    marginRight: '0.75rem',
    transition: 'color 0.2s'
  };

  const actionButtonStyle = {
    color: '#dc2626',
    textDecoration: 'none',
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: '500',
    transition: 'color 0.2s'
  };

  const dragHandleStyle = {
    cursor: 'grab',
    padding: '0.5rem',
    color: '#6b7280',
    fontSize: '1rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2rem',
    height: '2rem',
    borderRadius: '0.25rem',
    backgroundColor: '#f9fafb',
    border: '1px solid #e5e7eb',
    transition: 'all 0.2s'
  };

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td style={tableCellStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div
            {...listeners}
            style={dragHandleStyle} 
            title="Sürükleyerek sıralayın"
          >
            ⋮⋮
          </div>
          <div>
            <div style={{ fontWeight: '600', color: '#111827' }}>
              {bank.bank_name}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
              {bank.account_holder}
            </div>
          </div>
        </div>
      </td>
      <td style={tableCellStyle}>
        <span style={statusButtonStyle(bank.is_active)}>
          {bank.is_active ? 'Aktif' : 'Pasif'}
        </span>
      </td>
      <td style={tableCellStyle}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {bank.sort_order || 0}
        </div>
      </td>
      <td style={tableCellStyle}>
        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
          {new Date(bank.created_at).toLocaleDateString('tr-TR')}
        </div>
      </td>
      <td style={tableCellStyle}>
        <Link
          to={`/admin/banks/${bank.id}/edit`}
          style={actionLinkStyle}
          onMouseOver={(e) => e.target.style.color = '#1d4ed8'}
          onMouseOut={(e) => e.target.style.color = '#2563eb'}
        >
          Düzenle
        </Link>
        <button
          onClick={() => onDelete(bank.id)}
          style={actionButtonStyle}
          onMouseOver={(e) => e.target.style.color = '#b91c1c'}
          onMouseOut={(e) => e.target.style.color = '#dc2626'}
        >
          Sil
        </button>
      </td>
    </tr>
  );
};

const BanksList = () => {
  const [banks, setBanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    try {
      setLoading(true);
      const response = await banksApi.getAll();
      
      if (response.data.success) {
        setBanks(response.data.data || []);
        setError(null);
      } else {
        setError('Bankalar yüklenemedi');
      }
    } catch (err) {
      console.error('Banks fetch error:', err);
      setError('Bankalar yüklenirken hata oluştu: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu bankayı silmek istediğinizden emin misiniz? Bu işlem bankaya bağlı tüm hesapları da silecektir.')) {
      return;
    }

    try {
      const response = await banksApi.delete(id);
      if (response.data.success) {
        await fetchBanks();
        alert('Banka ve bağlı hesapları başarıyla silindi');
      } else {
        alert('Banka silinemedi');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Banka silinirken hata oluştu');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = banks.findIndex(item => item.id === active.id);
      const newIndex = banks.findIndex(item => item.id === over.id);
      
      const newBanks = arrayMove(banks, oldIndex, newIndex);
      setBanks(newBanks);

      try {
        // Update sort_order for all banks
        const updatePromises = newBanks.map((bank, index) => {
          return banksApi.update(bank.id, { sort_order: index + 1 });
        });

        await Promise.all(updatePromises);
        
        // Update local state with new sort_order values
        setBanks(prevBanks => 
          prevBanks.map((bank, index) => ({
            ...bank,
            sort_order: index + 1
          }))
        );
      } catch (error) {
        console.error('Error updating sort order:', error);
        // Revert on error
        await fetchBanks();
      }
    }
  };

  const containerStyle = {
    padding: '1.5rem',
    backgroundColor: '#f9fafb',
    minHeight: '100vh'
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem'
  };

  const titleStyle = {
    fontSize: '1.5rem',
    fontWeight: '700',
    color: '#111827',
    margin: 0
  };

  const buttonStyle = {
    backgroundColor: '#3b82f6',
    color: 'white',
    padding: '0.5rem 1rem',
    borderRadius: '0.375rem',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-block',
    transition: 'background-color 0.2s'
  };

  const tableStyle = {
    backgroundColor: 'white',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    borderRadius: '0.5rem',
    overflow: 'hidden',
    width: '100%',
    borderCollapse: 'collapse'
  };

  const tableHeaderStyle = {
    backgroundColor: '#f9fafb',
    padding: '0.75rem 1.5rem',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #e5e7eb'
  };

  const loadingStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '16rem',
    padding: '2rem'
  };

  const spinnerStyle = {
    width: '2rem',
    height: '2rem',
    border: '2px solid #e5e7eb',
    borderTop: '2px solid #3b82f6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  };

  const errorStyle = {
    textAlign: 'center',
    padding: '2rem',
    backgroundColor: 'white',
    borderRadius: '0.5rem',
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
  };

  const errorTextStyle = {
    color: '#dc2626',
    marginBottom: '1rem'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={loadingStyle}>
          <div style={spinnerStyle}></div>
          <p style={{ marginTop: '1rem', color: '#6b7280' }}>Bankalar yükleniyor...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={errorStyle}>
          <p style={errorTextStyle}>{error}</p>
          <button
            onClick={fetchBanks}
            style={{
              backgroundColor: '#ef4444',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '0.375rem',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            Tekrar Dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Bankalar - Admin Panel</title>
      </Helmet>
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h1 style={titleStyle}>Bankalar</h1>
          <Link to="/admin/banks/create" style={buttonStyle}>
            Yeni Banka Ekle
          </Link>
        </div>

        {banks.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
            Henüz banka bulunmamaktadır.
          </div>
        ) : (
          <div style={tableStyle}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Banka Bilgileri</th>
                    <th style={tableHeaderStyle}>Durum</th>
                    <th style={tableHeaderStyle}>Sıralama</th>
                    <th style={tableHeaderStyle}>Oluşturulma Tarihi</th>
                    <th style={tableHeaderStyle}>İşlemler</th>
                  </tr>
                </thead>
                <SortableContext
                  items={banks.map(bank => bank.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <tbody>
                    {banks.map((bank) => (
                      <SortableItem
                        key={bank.id}
                        bank={bank}
                        onEdit={() => {}}
                        onDelete={handleDelete}
                      />
                    ))}
                  </tbody>
                </SortableContext>
              </table>
            </DndContext>
          </div>
        )}
      </div>
    </>
  );
};

export default BanksList;
